import styled, { css } from 'styled-components';
import { Icon, IconSize, Text } from '@bechtel/gds';
import { Scalars } from '@bechtel/types';
import { TextWithEllipsis } from '@bechtel/client-common';
import { DraggableStateSnapshot } from 'react-beautiful-dnd';

interface PlanPermissionProps {
  canPlan: Scalars['Boolean'];
}

interface MilestoneHoursStyledProps {
  isSelected: boolean;
  isExpired: boolean;
}

type MilestoneStyledProps = PlanPermissionProps & MilestoneHoursStyledProps & { isGhosting: boolean };

export const StepContentStyled = styled.div`
  text-align: center;
  width: 100%;
  height: 150%;
`;

export const CardContainerStyled = styled.div<DraggableStateSnapshot>`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  ${({ theme }) => css`
    margin-bottom: ${theme.spacings[400]};
    padding: ${theme.spacings[450]};
    background: ${theme.colors.white};
    border-radius: ${theme.spacings[200]};
    box-shadow: 0 1px ${theme.spacings[200]} 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
      0 1px 1px 0 rgba(0, 0, 0, 0.14);
    border: solid 1px ${theme.colors.grey[200]};
  `}

  ${({ isDragging }) =>
    isDragging &&
    css`
      border-left: solid 4px ${({ theme }) => theme.colors.blue[700]};
    `}

  &:hover {
    box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.3), 0 2px 1px -1px rgba(0, 0, 0, 0.13), 0 1px 1px 0 rgba(0, 0, 0, 0.15);
  }
`;

export const IconCopiedStyled = styled(Icon)`
  position: absolute;
  left: 46%;
  top: -20px;
`;

export const TextWithEllipsisStyled = styled(TextWithEllipsis)`
  align-self: flex-start;
  padding-right: ${({ theme }) => theme.spacings[400]};
`;

export const IconCopyStyled = styled(Icon)`
  display: none;
  position: absolute;
  cursor: pointer;
  left: 48%;
  top: -16px;
  /* stylelint-disable */
  ${TextWithEllipsisStyled}:hover & {
    display: block !important;
  }
`;

export const CardHeaderStyled = styled.div<PlanPermissionProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  svg {
    transition-timing-function: ease-in-out;
    transition-duration: 100ms;
    transition-property: color, transform;
  }
  &:hover {
    ${(props: PlanPermissionProps) =>
      props.canPlan &&
      css`
        svg {
          transform: scale(1.1, 1.1);
          color: ${({ theme }) => theme.colors.blue[900]};
          opacity: 1;
        }
      `}
  }
`;

//GDS Box Styled Component replaced with styled.div for needed Draggable ref
export const MilestoneStyled = styled.div<MilestoneStyledProps>`
  display: flex;
  justify-content: space-between;
  ${({ theme }) => css`
    margin-bottom: ${theme.spacings[300]};
    padding: ${theme.spacings[400]};
    border: 1px solid ${theme.colors.grey[300]};
    border-radius: ${theme.spacings[200]};
  `}
  ${({ isGhosting }) =>
    isGhosting &&
    css`
      opacity: 0.4;
    `};
  svg {
    transition-timing-function: ease-in-out;
    transition-duration: 100ms;
    transition-property: color, transform;
  }
  &:hover {
    background-color: ${({ theme }) => theme.colors.blue[100]};
    ${({ canPlan }) =>
      canPlan &&
      css`
        svg {
          transform: scale(1.1, 1.1);
          opacity: 1;
        }
      `}
  }
  ${({ isExpired, theme }) =>
    isExpired &&
    css`
      &:hover {
        background-color: ${theme.colors.red[200]};
      }
      svg {
        color: ${theme.colors.red['000']};
      }
    `}
  ${({ isSelected, isExpired, theme }) => css`
    ${isSelected
      ? isExpired
        ? css`
            border: 1px solid ${theme.colors.red[500]};
            background-color: ${theme.colors.red[200]};
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
              0 1px 1px 0 rgba(0, 0, 0, 0.14);
            svg {
              opacity: 1;
              color: ${theme.colors.red['000']};
            }
            &:hover {
              background-color: ${theme.colors.red[200]};
            }
          `
        : css`
            border: 1px solid ${theme.colors.bechtel.blue[500]};
            background-color: ${theme.colors.bechtel.blue[100]};
            box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
              0 1px 1px 0 rgba(0, 0, 0, 0.14);
            svg {
              opacity: 1;
              color: ${theme.colors.bechtel.blue[800]};
            }
            &:hover {
              background-color: ${theme.colors.bechtel.blue[100]};
            }
          `
      : css``}
  `}
`;

export const MilestoneListContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
`;

export const GripIconStyled = styled(Icon).attrs({
  size: IconSize.XSMALL,
  iconStyle: 'solid',
})<MilestoneHoursStyledProps>`
  ${({ theme, isSelected, isExpired }) => css`
    color: ${theme.colors.blue[800]};
    ${isExpired &&
    css`
      color: ${theme.colors.red['000']};
    `}
    opacity: 0.5;
    ${isSelected &&
    css`
      opacity: 1;
    `}
  `}
`;

export const CommodityGripIconStyled = styled(Icon).attrs({
  size: IconSize.XSMALL,
  iconStyle: 'solid',
})`
  ${({ theme }) => css`
    color: ${theme.colors.blue[800]};
    opacity: 0.5;
  `}
`;

export const MilestoneSelectedCountStyled = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: absolute;
  ${({ theme }) => css`
    background-color: ${theme.colors.bechtel.blue[800]};
    color: ${theme.colors.white};
    border: 1px solid ${theme.colors.blue[500]};
    border-radius: ${theme.radii[900]};
    font-size: ${theme.fontSizes[300]};
    height: ${theme.fontSizes[700]};
    right: -${theme.spacings[500]};
    top: -${theme.spacings[450]};
    width: ${theme.fontSizes[700]};
  `};
`;

export const MilestoneTextStyled = styled(Text).attrs({
  size: '300',
})<MilestoneHoursStyledProps>`
  ${({ isSelected, isExpired, theme }) => css`
    ${isExpired
      ? isSelected
        ? css`
            color: ${theme.colors.red[700]};
            font-weight: 600;
          `
        : css`
            color: ${theme.colors.red['000']};
          `
      : isSelected
      ? css`
          color: ${theme.colors.bechtel.blue[700]};
          font-weight: 600;
        `
      : css`
          color: ${theme.colors.grey['1000']};
        `}
  `}
`;

export const MilestoneHoursStyled = styled(Text).attrs({
  size: '300',
})<MilestoneHoursStyledProps>`
  ${({ isSelected, isExpired, theme }) => css`
    ${isExpired
      ? isSelected
        ? css`
            color: ${theme.colors.red[700]};
            font-weight: 600;
          `
        : css`
            color: ${theme.colors.red['000']};
          `
      : isSelected
      ? css`
          color: ${theme.colors.bechtel.blue[700]};
          font-weight: 600;
        `
      : css`
          color: ${theme.colors.grey['600']};
        `}
  `}
`;
